import { lazy, Suspense, useRef } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { Outlet, useLocation } from "@tanstack/react-router"
import { authClient } from "@/lib/auth-client"
import { SiteHeader } from "@/components/SiteHeader"
import { ScrollToTop } from "@/components/ScrollToTop"
import { PublicFooter } from "@/components/PublicFooter"
import { LayoutModeProvider } from "@/lib/contexts/layout-mode-context"
import { ChatDialogProvider } from "@/lib/contexts/chat-dialog-context"
import { ExpandProvider } from "@/lib/contexts/expand-context"
import { PlaceDetailsProvider } from "@/lib/contexts/place-details-context"
import { ReorderProvider } from "@/lib/contexts/reorder-context"
import { VisibleDayProvider } from "@/lib/contexts/visible-day-context"
import { optionalSidebarSsr } from "@/lib/guide-ssr"
import { isTripOrGuidePage } from "@/lib/is-trip-or-guide-page"

// The single HeroUIProvider + ToastProvider mount, loaded lazily so its ~249 KB
// barrel stays off the public read/explore critical path (nothing in the explore
// grid or the SSR'd guide article uses HeroUI). Mounted ONLY around the authed
// shell here; the public guide read view mounts it itself at the TripPage gate.
const LazyHeroProvider = lazy(() => import(`@/components/LazyHeroProvider`))

// The authed shell (sidebar, trip-create recede sheet, dialogs) is rendered ONLY
// in the authed branch, but a static import dragged its whole graph
// (recede-sheet/motion, ProUpgradeDialog, …) onto the logged-out cold wave for
// explore + the public guide read — the bulk of their "unused JavaScript". Lazy
// + the existing Suspense keeps it off that wave entirely.
const AuthenticatedShell = lazy(() =>
  import(`@/components/AuthenticatedShell`).then((m) => ({
    default: m.AuthenticatedShell,
  }))
)

export const Route = createFileRoute(`/_with-optional-sidebar`)({
  // Mirror the root: this layout sits between root and the guide-detail /
  // /trips/explore routes, so it too must resolve to non-false for those URLs or
  // it would force the leaves back to ssr:false. For those URLs the component
  // now DOES render on the server (full SSR) — see the isPending bypass below.
  ssr: optionalSidebarSsr,
  component: OptionalSidebarLayout,
})

function OptionalSidebarLayout() {
  const { data: session, isPending } = authClient.useSession()
  const location = useLocation()
  const showPublicFooter = location.pathname === `/trips/explore`
  const mainRef = useRef<HTMLElement>(null)

  // The SSR'd anon-first public reads under this layout (explore hub + the
  // public guide read view). On the server `useSession` is always pending, so
  // without this bypass the placeholder below would be the only HTML the crawler
  // ever sees — defeating the whole point of SSRing the article/grid. These
  // pages render fine logged-out (the logged-out branch's plain Header), and a
  // signed-in visitor's first client render is also pending → same branch → no
  // hydration mismatch; the authed shell swaps in once the session resolves.
  const isPublicRead =
    location.pathname === `/trips/explore` ||
    (isTripOrGuidePage(location.pathname) === false &&
      location.pathname.startsWith(`/guide/`))

  // A pulsing placeholder avoids a CLS jump: returning null collapses the
  // layout to zero height while the session resolves, then the real content
  // drops in and shifts everything below it. Skipped for the SSR'd public reads.
  if (isPending && !isPublicRead)
    return <div className="min-h-screen bg-background animate-pulse" />

  // The authed shell is interactive (dialogs, toasts, the planner) so it pays for
  // HeroUI — but lazily, behind Suspense, so the barrel isn't in the first wave.
  // The logged-out branch below ships NO HeroUI: the explore grid and the SSR'd
  // guide article don't use it, and the guide-interactive (TripPage) path mounts
  // its own LazyHeroProvider at the gate (see the guide route).
  if (session) {
    return (
      <Suspense
        fallback={<div className="min-h-screen bg-background animate-pulse" />}
      >
        <LazyHeroProvider>
          <PlaceDetailsProvider>
            <AuthenticatedShell>
              <Outlet />
              {showPublicFooter ? <PublicFooter /> : null}
            </AuthenticatedShell>
          </PlaceDetailsProvider>
        </LazyHeroProvider>
      </Suspense>
    )
  }

  // If not authenticated, show with header. VisibleDayProvider must be here
  // too (the authed branch gets it via AuthenticatedShell): without it the
  // guide map's "Current Day" pin filter is a no-op — reportIntersection
  // falls back to the default context and visibleDayId stays null.
  return (
    <PlaceDetailsProvider>
      <VisibleDayProvider>
        <ReorderProvider>
          <LayoutModeProvider>
            <ExpandProvider>
              <ChatDialogProvider>
                <div
                  className="flex flex-col h-dvh w-full"
                  style={
                    { "--header-height": `3.25rem` } as React.CSSProperties
                  }
                >
                  <SiteHeader />
                  <main
                    ref={mainRef}
                    className="w-full flex-1 overflow-y-auto pt-[var(--header-height)]"
                  >
                    {/* The logged-out shell scrolls this <main>, not the window —
                      without the reset, explore → guide kept explore's scroll
                      offset (the authed shell already does this). */}
                    <ScrollToTop scrollRef={mainRef} />
                    <Outlet />
                    {showPublicFooter ? <PublicFooter /> : null}
                  </main>
                </div>
              </ChatDialogProvider>
            </ExpandProvider>
          </LayoutModeProvider>
        </ReorderProvider>
      </VisibleDayProvider>
    </PlaceDetailsProvider>
  )
}
